"use client";
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/typeof';
import * as React from 'react';
import { autocomplete, inputWrap } from './styles.js';
import { useOutsideClickClose } from '../utils/hooks/useOutsideClickClose.web.js';
import { useBoolean } from '../utils/hooks/useBoolean.js';
import { callOptional } from '../utils/helpers/function.js';
import { ThemeProvider } from 'styled-components';
import { useForkRef } from '../utils/hooks/useForkRef.js';
import { InputField } from '../InputField/InputField.web.js';
import { ListItems } from '../utils/components/ListItems/ListItems.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { useSmartControlledEventHandler } from '../utils/hooks/useSmartControlledEventHandler.web.js';

var _excluded = ["borderStyle", "borderRadius", "labelStyle", "showItems", "value", "onChange", "onSelectItem", "disabled", "label", "id", "name", "onBlur", "onFocus", "readOnly", "items", "maxItems", "leftIcon", "onLeftIconClick", "leftIconAriaLabel", "rightIcon", "onRightIconClick", "rightIconAriaLabel", "filterItems", "listOffset", "renderItem", "type", "error", "errorMessage", "color", "required", "inputMode", "inputRef", "inputProps", "placeholder", "variant", "size", "maxLength"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var defaultFilterItems = function defaultFilterItems(items, filter) {
  if (typeof filter !== 'string') {
    console.warn("Cannot filter in AutoComplete: provided value prop was typeof ".concat(_typeof(filter), " - string expected"));
    return items;
  }
  return items.filter(function (item) {
    return filter && (typeof item === 'string' ? item.toLowerCase() : item.name.toLowerCase()).includes(filter.toLowerCase());
  });
};
var filterItems = function filterItems(items, filter, customFilterItems, maxItems) {
  if (!items) return [];
  var filtered = customFilterItems ? customFilterItems(items, filter) : defaultFilterItems(items, filter);
  var itemized = filtered.map(function (item) {
    return typeof item === 'string' ? {
      value: item,
      name: item
    } : item;
  });
  return itemized.slice(0, maxItems || filtered.length);
};
var AutoComplete = function AutoComplete(_ref) {
  var borderStyleProp = _ref.borderStyle,
    borderRadiusProp = _ref.borderRadius;
    _ref.labelStyle;
    var _ref$showItems = _ref.showItems,
    showItems = _ref$showItems === undefined ? 5 : _ref$showItems,
    valueProp = _ref.value,
    onChangeProp = _ref.onChange,
    onSelectItem = _ref.onSelectItem,
    disabled = _ref.disabled,
    label = _ref.label,
    id = _ref.id,
    name = _ref.name,
    onBlur = _ref.onBlur,
    onFocus = _ref.onFocus,
    readOnly = _ref.readOnly,
    items = _ref.items,
    maxItems = _ref.maxItems,
    leftIcon = _ref.leftIcon,
    onLeftIconClick = _ref.onLeftIconClick,
    leftIconAriaLabel = _ref.leftIconAriaLabel,
    rightIcon = _ref.rightIcon,
    onRightIconClick = _ref.onRightIconClick,
    rightIconAriaLabel = _ref.rightIconAriaLabel,
    filterItemsProp = _ref.filterItems,
    listOffset = _ref.listOffset,
    renderItem = _ref.renderItem;
    _ref.type;
    var error = _ref.error,
    errorMessage = _ref.errorMessage,
    color = _ref.color,
    required = _ref.required,
    inputMode = _ref.inputMode,
    inputRefProp = _ref.inputRef,
    inputProps = _ref.inputProps,
    placeholder = _ref.placeholder,
    variantProp = _ref.variant,
    sizeProp = _ref.size,
    maxLength = _ref.maxLength,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useBoolean = useBoolean(false),
    opened = _useBoolean.value,
    handleClose = _useBoolean.setFalse,
    handleOpen = _useBoolean.setTrue;
  var theme = useTheme();
  var variant = variantProp || theme.variant;
  var _useOutsideClickClose = useOutsideClickClose(handleClose),
    wrapperRef = _useOutsideClickClose.wrapperRef;
  var internalInputRef = React.useRef(null);
  // istanbul ignore next -- implementation detail
  var inputRef = useForkRef(inputRefProp, internalInputRef);
  var borderStyle = variant === 'condensed' ? theme.inputField.web.variants.condensed.borderStyle : borderStyleProp ? borderStyleProp : theme.inputField.web.borderStyle;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focused = _React$useState2[0],
    setFocused = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    disableActive = _React$useState4[0],
    setDisableActive = _React$useState4[1];
  var _React$useState5 = React.useState(undefined),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    activeListElement = _React$useState6[0],
    setActiveListElement = _React$useState6[1];
  var _useSmartControlledEv = useSmartControlledEventHandler('', valueProp, onChangeProp),
    _useSmartControlledEv2 = _slicedToArray(_useSmartControlledEv, 2),
    value = _useSmartControlledEv2[0],
    onChange = _useSmartControlledEv2[1];
  var handleAutocompleteBlur = React.useCallback(function (e) {
    // if focus is transfered outside of autocomplete - close
    if (e.relatedTarget && wrapperRef.current && wrapperRef.current.contains(e.relatedTarget)) {
      return;
    }
    var crafted = _objectSpread(_objectSpread({}, e), {}, {
      target: _objectSpread(_objectSpread({}, e.target), {}, {
        // we dont want to pass list item target because it makes little sense
        // focus doesn't need this same shuffle because when focusing in input is the only target available
        value: internalInputRef.current && internalInputRef.current.value || /* istanbul ignore next */'' // just sanity in case ref is undefined
      })
    });

    // this is some weird react behavior regarding onBlur -- check https://gist.github.com/pstoica/4323d3e6e37e8a23dd59
    setTimeout(function () {
      setFocused(false);
      callOptional(onBlur)(crafted);
      handleClose();
    });
  }, [handleClose, onBlur, wrapperRef]);
  var handleAutocompleteFocus = React.useCallback(function (e) {
    // if focus is transfered in FROM outside of autocomplete - open
    if (e.relatedTarget && wrapperRef.current && wrapperRef.current.contains(e.relatedTarget)) {
      return;
    }
    setFocused(true);
    callOptional(onFocus)(e);
    handleOpen();
  }, [handleOpen, onFocus, wrapperRef]);
  var handleInputKeyDown = React.useCallback(function (e) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter' && e.key !== 'Escape') {
      handleOpen();
      setDisableActive(true);
    } else {
      setDisableActive(false);
    }
  }, [handleOpen, setDisableActive]);
  var handleListInputChange = React.useCallback(function (value) {
    // istanbul ignore else
    if (internalInputRef.current) {
      internalInputRef.current.focus();
    }

    // TODO: this is not a real change event
    onChange({
      target: {
        name: name,
        value: value
      },
      currentTarget: {
        name: name,
        value: value
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    });
    callOptional(onSelectItem)(value);
    handleClose();
  }, [onChange, handleClose, name, onSelectItem]);
  var handleCloseFromList = React.useCallback(function () {
    handleClose();
    // istanbul ignore else

    if (inputRef && internalInputRef.current) {
      internalInputRef.current.focus();
    }
  }, [handleClose, inputRef]);
  var filteredItems = React.useMemo(function () {
    return filterItems(items, value, filterItemsProp, maxItems);
  }, [items, value, filterItemsProp, maxItems]);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(autocomplete, _extends({
    "data-testid": "elemental-AutoComplete-".concat(id || name),
    ref: wrapperRef,
    $opened: opened,
    onFocusCapture: handleAutocompleteFocus,
    onBlurCapture: handleAutocompleteBlur,
    borderRadius: borderRadiusProp
  }, props), /*#__PURE__*/React.createElement(inputWrap, {
    $color: color ? color : theme.inputField.web.color,
    $opened: opened
  }, /*#__PURE__*/React.createElement(InputField, {
    autoComplete: "off",
    disabled: disabled,
    inputRef: inputRef,
    label: label,
    id: id,
    name: name,
    onKeyDown: handleInputKeyDown,
    onChange: onChange,
    onClick: handleOpen,
    leftIcon: leftIcon,
    onLeftIconClick: onLeftIconClick,
    leftIconAriaLabel: leftIconAriaLabel,
    rightIcon: rightIcon,
    onRightIconClick: onRightIconClick,
    rightIconAriaLabel: rightIconAriaLabel,
    readOnly: readOnly,
    value: value,
    borderStyle: borderStyle,
    error: !focused && error,
    errorMessage: errorMessage,
    color: color,
    required: required,
    placeholder: placeholder,
    type: "search",
    disableBottomBorder: filteredItems.length > 0 && opened,
    disableBottomBorderRadius: filteredItems.length > 0 && opened,
    variant: variant,
    size: sizeProp,
    maxLength: maxLength,
    inputProps: _objectSpread({
      'aria-expanded': opened && filteredItems.length > 0,
      role: 'combobox',
      'aria-controls': opened ? "elemental-Autocomplete-results-".concat(id || name) : undefined,
      'aria-autocomplete': 'list',
      'aria-activedescendant': opened ? activeListElement === null || activeListElement === undefined ? undefined : activeListElement.id : undefined,
      inputMode: inputMode
    }, inputProps)
  })), !readOnly && !disabled && /*#__PURE__*/React.createElement(ListItems, {
    id: "elemental-Autocomplete-results-".concat(id || name),
    "aria-label": label,
    disableFocusTransfer: true,
    highlightValue: value,
    items: filteredItems,
    onSelect: handleListInputChange,
    onClose: handleCloseFromList,
    onActiveChange: function onActiveChange(el) {
      return setActiveListElement(el);
    },
    borderRadius: borderRadiusProp || theme.borders.radius,
    opened: opened,
    color: color,
    showItems: showItems,
    renderItem: renderItem,
    size: sizeProp,
    offset: listOffset || (borderStyle === 'bottomOnly' ? 2 : undefined),
    selectWithSpacebar: false,
    disableActive: disableActive,
    variant: variant
  })));
};

export { AutoComplete, defaultFilterItems };
